import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { useState } from 'react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PricingCard = ({ name, price, period, words, features, popular, packageId, isCurrentPlan }) => {
  const { user, login } = useAuth();
  const [loading, setLoading] = useState(false);

  const handleCheckout = async () => {
    if (!user) {
      login();
      return;
    }

    setLoading(true);
    try {
      // PayFast checkout only
      const response = await axios.post(
        `${API}/payfast/onsite-session`,
        {
          package_id: packageId,
          origin_url: window.location.origin
        },
        { withCredentials: true }
      );

      const { uuid, engine_js } = response.data;

      // Load PayFast engine script if not already loaded
      if (!window.PayFast) {
        const script = document.createElement('script');
        script.src = engine_js;
        script.defer = true;
        script.onload = () => {
          if (window.PayFast && uuid) {
            window.PayFast.start({ uuid });
          }
        };
        document.body.appendChild(script);
      } else {
        window.PayFast.start({ uuid });
      }
    } catch (error) {
      toast.error('Error creating checkout session');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className={`p-8 border-2 relative ${popular ? 'border-primary' : 'border-zinc-200'}`}>
      {popular && (
        <Badge className="absolute -top-3 left-1/2 -translate-x-1/2">Most Popular</Badge>
      )}
      <div className="text-center mb-6">
        <h3 className="text-2xl font-heading font-bold mb-2">{name}</h3>
        <div className="mb-4">
          <span className="text-4xl font-heading font-black">R{price}</span>
          <span className="text-muted-foreground">/{period}</span>
        </div>
        <p className="text-sm text-muted-foreground">{words.toLocaleString()} words/month</p>
      </div>

      <ul className="space-y-3 mb-6">
        {features.map((feature) => (
          <li key={feature} className="flex items-start gap-2">
            <Check className="h-5 w-5 text-accent shrink-0 mt-0.5" />
            <span className="text-sm">{feature}</span>
          </li>
        ))}
      </ul>

      <Button
        className="w-full"
        variant={popular ? 'default' : 'outline'}
        onClick={handleCheckout}
        disabled={loading || isCurrentPlan}
        data-testid={`pricing-${packageId}-btn`}
      >
        {isCurrentPlan ? 'Current Plan' : loading ? 'Processing...' : 'Get Started'}
      </Button>
    </Card>
  );
};

const Pricing = () => {
  const { user } = useAuth();

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <div className="flex-1 py-24">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4" variant="secondary">Pricing</Badge>
            <h1 className="text-4xl md:text-6xl font-heading font-black tracking-tighter mb-6">
              Choose Your Plan
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Start free, upgrade when you need more. All plans include access to our AI humanizer.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <PricingCard
              name="Free"
              price="0"
              period="forever"
              words={5000}
              features={[
                '5,000 words per month',
                'All humanization styles',
                'Multi-detector checking',
                'Basic support',
                'Save your history'
              ]}
              packageId="free"
              isCurrentPlan={user?.tier === 'free'}
            />

            <PricingCard
              name="Monthly"
              price="359"
              period="month"
              words={50000}
              features={[
                '50,000 words per month',
                'All humanization styles',
                'Multi-detector checking',
                'Priority support',
                'Unlimited history',
                'Bulk processing',
                'API access'
              ]}
              popular
              packageId="monthly"
              isCurrentPlan={user?.tier === 'monthly'}
            />

            <PricingCard
              name="Annual"
              price="1799"
              period="year"
              words={100000}
              features={[
                '100,000 words per month',
                'All humanization styles',
                'Multi-detector checking',
                'Priority support',
                'Unlimited history',
                'Bulk processing',
                'API access',
                'Save 50% vs monthly'
              ]}
              packageId="annual"
              isCurrentPlan={user?.tier === 'annual'}
            />
          </div>

          {/* FAQ */}
          <div className="mt-24 max-w-3xl mx-auto">
            <h2 className="text-3xl font-heading font-bold text-center mb-12">Frequently Asked Questions</h2>
            <div className="space-y-6">
              <div>
                <h3 className="font-heading font-bold mb-2">How does the word count work?</h3>
                <p className="text-muted-foreground">Words are counted from your input text. Your limit resets at the start of each billing cycle.</p>
              </div>
              <div>
                <h3 className="font-heading font-bold mb-2">Can I cancel anytime?</h3>
                <p className="text-muted-foreground">Yes! Cancel anytime from your dashboard. You'll keep access until the end of your billing period.</p>
              </div>
              <div>
                <h3 className="font-heading font-bold mb-2">What payment methods do you accept?</h3>
                <p className="text-muted-foreground">We use PayFast, South Africa's leading payment gateway. Pay with credit cards, debit cards, or instant EFT in ZAR.</p>
              </div>
              <div>
                <h3 className="font-heading font-bold mb-2">Is there a guarantee?</h3>
                <p className="text-muted-foreground">Our humanizer is tested against all major AI detectors. If you're not satisfied, contact us within 30 days.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Pricing;
